import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import React, { useEffect, useMemo, useState, useCallback } from "react";


import {
  clearMessage,
  getHistories,
  deleteHistory,
  clearHistory,
} from "@/services/historiesServices/historiesActions";

import NotificationAlertOptions from "@/layouts/Alerts";
import ReactBSAlert from "react-bootstrap-sweetalert";
import DisplayHistoriesView from "./DisplayHistoriesView";
import type { HistoryEntry } from "./DisplayHistoriesView";
import { asCaught } from "@/services/caughtError";

const DisplayHistoriesContainer = () => {
  const dispatch = useAppDispatch();

  // store
  const historiesState = useAppSelector((s) => s.histories);
  const histories: HistoryEntry[] = useAppSelector((s) => s.histories?.histories || []);
  const permission = useCurrentPermission();

  // ui
  const [openedCollapses, setOpenedCollapses] = useState(["collapseTwo"]); // closed by default
  const [alertNode, setAlertNode] = useState<React.ReactNode>(null);

  // fetch on mount
  useEffect(() => {
    dispatch(getHistories());
  }, [dispatch]);

  // success messages 
  useEffect(() => {
    if (historiesState?.message) {
      NotificationAlertOptions("success", historiesState.message);
      dispatch(clearMessage());
    }
  }, [historiesState?.message, dispatch]);

  const hideAlert = useCallback(() => setAlertNode(null), []);

  const questionAlert = useCallback(
    (e: React.MouseEvent, element: HistoryEntry) => {
      e?.preventDefault?.();
      setAlertNode(
        // @ts-expect-error react-bootstrap-sweetalert prop-types (customIcon/btnSize)
        <ReactBSAlert
          custom
          style={{ display: "block", marginTop: "-100px" }}
          title="Would you like to remove this history?"
          customIcon={
            <div
              className="swal2-icon swal2-question swal2-animate-question-icon"
              style={{ display: "flex" }}
            >
              <span className="swal2-icon-text">?</span>
            </div>
          }
          onConfirm={async () => {
            // Same shape as clearHistoryAlert below: confirm only after the delete
            // resolved successfully; a failure toasts via the errors effect (SCRUM-1944).
            const deleted = await dispatch(deleteHistory(element._id));
            hideAlert();
            if (deleted) NotificationAlertOptions("success", "History Deleted");
          }}
          onCancel={hideAlert}
          confirmBtnBsStyle="default"
          confirmBtnText="Ok"
          btnSize=""
        >
          Delete Operation
        </ReactBSAlert>
      );
    },
    [dispatch, hideAlert]
  );

  const clearHistoryAlert = useCallback(() => {
    setAlertNode(
      // @ts-expect-error react-bootstrap-sweetalert prop-types (customIcon/btnSize)
      <ReactBSAlert
        custom
        style={{ display: "block", marginTop: "-100px" }}
        title="Would you like to remove all the histories?"
        customIcon={
          <div
            className="swal2-icon swal2-question swal2-animate-question-icon"
            style={{ display: "flex" }}
          >
            <span className="swal2-icon-text">?</span>
          </div>
        }
        onConfirm={async () => {
          // Only show success when the clear succeeds; clearHistory now re-throws on
          // failure (SCRUM-815).
          try {
            await dispatch(clearHistory());
            hideAlert();
            NotificationAlertOptions("success", "History Deleted");
          } catch (err) {
            hideAlert();
            NotificationAlertOptions(
              "error",
              asCaught(err)?.response?.data?.message || "Deleting the history failed. Please try again."
            );
          }
        }}
        onCancel={hideAlert}
        confirmBtnBsStyle="default"
        confirmBtnText="Ok"
        btnSize=""
      >
        Delete Operation
      </ReactBSAlert>
    );
  }, [dispatch, hideAlert]);

  const collapsesToggle = useCallback((collapseKey: string) => {
    setOpenedCollapses((opened) =>
      opened.includes(collapseKey) ? [] : [collapseKey]
    );
  }, []);

  const ViewModel = useMemo(
    () => ({
      alertNode,
      histories,
      permission,
      openedCollapses,
      collapsesToggle,
      questionAlert,
      clearHistoryAlert,
    }),
    [
      alertNode,
      histories,
      permission,
      openedCollapses,
      collapsesToggle,
      questionAlert,
      clearHistoryAlert,
    ]
  );

  return <DisplayHistoriesView {...ViewModel} />;
};

export default DisplayHistoriesContainer;
